import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ListTodo, Clock4, Layers3, CheckCircle2, Users, Briefcase } from "lucide-react";
import Layout from "../components/Layout";
import KpiCard from "../components/KpiCard";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const STAGE_PILL = {
  "New": "bg-blue-50 text-blue-700",
  "Junk": "bg-slate-100 text-slate-600",
  "Not Connected": "bg-slate-100 text-slate-700",
  "Follow-up": "bg-indigo-50 text-indigo-700",
  "Interested": "bg-amber-50 text-amber-700",
  "Not-Interested": "bg-rose-50 text-rose-700",
  "Contact-in-Future": "bg-violet-50 text-violet-700",
  "Agreement In-Process": "bg-orange-50 text-orange-700",
  "Onboarding-in-Process": "bg-orange-100 text-orange-800",
  "Live": "bg-emerald-50 text-emerald-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [pickable, setPickable] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get("/reports/summary"),
          api.get("/leads?pickable=true&limit=10"),
        ]);
        setSummary(s.data);
        setPickable(p.data?.items || []);
      } catch {}
    })();
  }, []);

  return (
    <Layout
      title={`Welcome back, ${(user?.name || "User").split(" ")[0]}!`}
      breadcrumb={["Home", "Dashboard"]}
    >
      {/* Hero card */}
      <div className="peach-bg border border-orange-100 rounded-2xl p-6 mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-orange-700 font-semibold">
            Lead Management System
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1 tracking-tight">
            Today’s pipeline at a glance
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Track new captures, qualify your hot leads, and convert faster with disciplined stage SOPs.
          </p>
        </div>
        <Link
          to="/leads?create=1"
          data-testid="dashboard-add-lead-btn"
          className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2.5 rounded-md flex items-center gap-1.5 shadow-sm"
        >
          <Plus size={14} /> Add a lead
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          testid="kpi-total-leads"
          title="Total leads"
          value={summary?.total_leads ?? "—"}
          hint={`This month: ${summary?.new_this_month ?? 0}`}
          accent="orange"
        />
        <KpiCard
          testid="kpi-new-leads"
          title="New leads"
          value={summary?.new_leads ?? "—"}
          hint="Awaiting first call"
          accent="blue"
        />
        <KpiCard
          testid="kpi-in-pipeline"
          title="In pipeline"
          value={summary?.in_pipeline ?? "—"}
          hint="Active opportunities"
          accent="violet"
        />
        <KpiCard
          testid="kpi-live-accounts"
          title="Live accounts"
          value={summary?.live_accounts ?? "—"}
          hint="Converted"
          accent="green"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Action items + Pipeline grid */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Pipeline overview</h3>
              <Link to="/leads" className="text-xs text-orange-600 hover:underline">View all</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {(summary?.by_stage || []).map((s) => (
                <Link
                  key={s.stage}
                  to={`/leads?lead_stage=${encodeURIComponent(s.stage)}`}
                  data-testid={`pipeline-tile-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block rounded-xl p-3 border border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all"
                >
                  <div className={`text-2xl font-bold ${STAGE_PILL[s.stage]?.split(" ").pop() || "text-slate-900"}`}>
                    {s.count}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-tight">{s.stage}</div>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-slate-900">Today’s action items</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ActionTile
                icon={<ListTodo size={18} />}
                label="New leads to call"
                value={summary?.new_leads ?? 0}
                color="bg-emerald-50 text-emerald-700"
                to="/leads?lead_stage=New"
              />
              <ActionTile
                icon={<Clock4 size={18} />}
                label="Pickable leads"
                value={pickable.length}
                color="bg-orange-50 text-orange-700"
                to="/leads?pickable=true"
              />
              <ActionTile
                icon={<Layers3 size={18} />}
                label="My open leads"
                value={summary?.my_leads ?? 0}
                color="bg-blue-50 text-blue-700"
                to="/leads?mine=true"
              />
            </div>
          </div>
        </div>

        {/* Right column: pickable list */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm h-fit">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900">Pickable leads</h3>
            <Link to="/leads?pickable=true" className="text-xs text-orange-600 hover:underline">
              See all
            </Link>
          </div>
          {pickable.length === 0 && (
            <div className="text-sm text-slate-500 py-6 text-center">No pickable leads right now.</div>
          )}
          <ul className="space-y-2.5">
            {pickable.slice(0, 6).map((l) => (
              <li key={l.id}>
                <Link
                  to={`/leads/${l.id}`}
                  data-testid={`pickable-row-${l.id}`}
                  className="flex items-center justify-between gap-3 p-2.5 rounded-md hover:bg-slate-50 border border-slate-100"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{l.company_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {l.lead_stage} • Last owner: {l.lead_owner_name || "—"}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                    Pick
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
}

function ActionTile({ icon, label, value, color, to }) {
  return (
    <Link
      to={to}
      data-testid={`action-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex items-center gap-3 p-4 rounded-lg border border-slate-100 hover:border-orange-200 hover:shadow-sm transition-all"
    >
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${color}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-bold text-slate-900">{value}</div>
      </div>
    </Link>
  );
}
